import loadable from "@loadable/component"
import { Suspense, useEffect, useRef, useState } from "react"
import { Helmet } from "react-helmet-async"
import { useDispatch, useSelector } from "react-redux"
import { Link } from "react-router-dom"

import { setExperiment } from "app-store-v2/app/actions"
import ThresholdProgressBar from "components/threshold-progress-bar"
import Flashbar from "components/ui/flashbar"
import Image from "components/ui/image"
import Media from "components/ui/media"
import Shimmer from "components/ui/shimmer"
import { jewlr_t, isTestMode, retry, IS_JEWLR } from "helpers/application"
import { freeGiftPromoEnabled } from "helpers/holiday"
import useWindowDimensions from "helpers/window-dimensions"

import CartLoading from "./cart-loading"
import CartTitle from "./cart-title"
import { MainContainer, SidePanelWrapper } from "./components"
import EmptyCart from "./empty-cart"
import AddonDesktop from "./exclusive-addons/addon-desktop"
import ExclusiveAddonsDesktop from "./exclusive-addons/desktop"
import ExclusiveAddons from "./exclusive-addons/mobile"
import Footer from "./footer"
import FreeGiftsDesktop from "./free-gifts/desktop"
import FreeGifts from "./free-gifts/mobile"
import fetchCart from "./hoc/fetch-cart"
import LineItems from "./line-items"
import OutOfStockBanner from "./out-of-stock-banner"
import PromoCode from "./promo-code"
import Promos from "./promos"
import SidePanel from "./side-panel"

const DetailModal = loadable(
  () => retry(() => import("./exclusive-addons/detail-modal")),
  { ssr: false }
)
const ErrorModal = loadable(
  () => retry(() => import("areas/checkout/checkout/modal/error-modal")),
  { ssr: false }
)
const GiftPreviewVideoModal = loadable(
  () => retry(() => import("./line-items/gift-preview-video")),
  { ssr: false }
)

const Cart = () => {
  const [showAddonModal, toggleAddonModal] = useState(false)
  const [addonInModal, setAddonInModal] = useState({})
  const [disableActions, toggleDisableActions] = useState({
    action: "",
    id: "",
  })
  const refs = {
    cartSummaryRef: useRef(),
    checkoutBtnRef: useRef(),
    desktopSummaryRef: useRef(),
  }

  const cartData = useSelector((state) => state.cart)
  const cartLoaded = cartData?.cartLoaded
  const couponApplied = cartData?.summary?.promo_code?.promo_code_applied
  const cartWarrantyExp = useSelector((state) => state.experiment.cartWarranty)
  const dispatch = useDispatch()

  const { isMobile } = useWindowDimensions()
  let content = null

  const freeGiftPromoApplied = () =>
    !sl.config.free_gift_promo?.code ||
    couponApplied?.toUpperCase() ===
      sl.config.free_gift_promo.code?.toUpperCase()

  useEffect(() => {
    if (!isTestMode) {
      dataLayer.push({
        event: "viewed-cart",
      })
    }
  }, [])

  useEffect(() => {
    if (typeof window.Beacon !== "undefined") {
      window.Beacon("config", { hideFABOnMobile: true })
    }
    return () => {
      if (typeof window.Beacon !== "undefined") {
        window.Beacon("config", { hideFABOnMobile: false })
      }
    }
  }, [isMobile])

  useEffect(() => {
    if (cartWarrantyExp) {
      dispatch(
        setExperiment({
          cartWarranty: cartWarrantyExp,
        })
      )
    }
  }, [cartWarrantyExp])

  if (!cartLoaded) {
    content = (
      <div>
        <div className="bg-grey-4 contained tablet:mb-12 tablet:bg-white tablet:pt-0 flex flex-col px-16 pt-10">
          <CartTitle cartData={cartData} refs={refs} />
          <div className="tablet:-order-1">
            <Shimmer
              className="tablet:w-(--desktop-width) mx-auto my-32 w-full rounded-lg"
              style={{
                "--desktop-width": "740px",
                height: "75px",
              }}
            />
          </div>
        </div>
        <div className="bg-grey-4 tablet:bg-white contained tablet:mb-0 mb-16 px-16">
          <CartLoading />
        </div>
      </div>
    )
  } else if (cartData.line_items.length == 0) {
    content = <EmptyCart />
  } else {
    content = (
      <>
        <div className="contained bg-grey-4 tablet:bg-white tablet:mb-12 tablet:pt-0 flex flex-col px-16 pt-10">
          {sl.config.sale_key.includes("2025_jewlr_days_flash") && (
            <Media className="mb-16 mt-32" greaterThanOrEqual="desktop">
              <ThresholdProgressBar />
            </Media>
          )}
          <CartTitle cartData={cartData} refs={refs} />
          {cartData.summary.has_out_of_stock_item && (
            <div className="tablet:-order-1">
              <OutOfStockBanner />
            </div>
          )}
          <div className="tablet:-order-2">
            <Promos refs={refs} />
          </div>
          {sl.config.sale_key.includes("2025_jewlr_days_flash") && (
            <Media className="tablet:mt-10" lessThan="desktop">
              <ThresholdProgressBar />
            </Media>
          )}
        </div>
        <div className="contained bg-grey-4 tablet:bg-white tablet:mb-0 mb-16 px-16">
          {cartData.err_msg && (
            <Flashbar message={cartData.err_msg} type="error" />
          )}
          <div className="desktop:flex-row desktop:gap-16 flex flex-col gap-40">
            <MainContainer>
              {freeGiftPromoEnabled() && (
                <>
                  {freeGiftPromoApplied() && <FreeGiftsDesktop />}
                  <div className="tablet:hidden -mt-20 mb-24">
                    <PromoCode promo />
                    {freeGiftPromoApplied() && <FreeGifts />}
                  </div>
                </>
              )}
              <LineItems line_items={cartData.line_items} />
              {/* <FreeGiftsDesktop />
            <FreeGifts /> */}
              <AddonDesktop exclusiveAddonsRef={refs.exclusiveAddonsRef} />
              {IS_JEWLR() && (
                <ExclusiveAddons
                  disableActions={disableActions}
                  setAddonInModal={setAddonInModal}
                  toggleAddonModal={toggleAddonModal}
                  toggleDisableActions={toggleDisableActions}
                />
              )}
              {sl.config.env !== "production" && IS_JEWLR() && (
                <div className="tablet:hidden">
                  <Link data-lc="cart" to="/">
                    <Image
                      alt={`${jewlr_t("terms.Personalize")} The Perfect Gift`}
                      className="w-full"
                      height="569"
                      src={require("images/cart/ad-block.jpg")}
                      width="672"
                    />
                  </Link>
                </div>
              )}
            </MainContainer>
            <SidePanelWrapper>
              <SidePanel refs={refs} />
            </SidePanelWrapper>
          </div>
          {IS_JEWLR() && (
            <ExclusiveAddonsDesktop
              disableActions={disableActions}
              refs={refs}
              setAddonInModal={setAddonInModal}
              toggleAddonModal={toggleAddonModal}
              toggleDisableActions={toggleDisableActions}
            />
          )}
        </div>
        <Footer />
        {typeof window !== "undefined" && (
          <Suspense fallback={<div />}>
            {sl.config.enable_gift_preview_video && <GiftPreviewVideoModal />}
            <ErrorModal />
            <DetailModal
              addon={addonInModal}
              disableActions={disableActions}
              show={showAddonModal}
              toggleAddonModal={toggleAddonModal}
              toggleDisableActions={toggleDisableActions}
            />
          </Suspense>
        )}
      </>
    )
  }

  return (
    <>
      <Helmet>
        <meta content="noindex" name="robots" />
      </Helmet>
      {content}
    </>
  )
}

export default fetchCart(Cart)
